import { useEffect, useState } from 'react';
import { DOCX_MIME_TYPE } from './document-viewer/document-viewer-registry';
import { DocxPreviewRenderer } from './DocxPreviewRenderer';

type CodegenDocxPanelPreviewProps = {
  filename: string;
  /** Renvoie une URL neuve : les URL présignées expirent pendant qu'un panneau reste ouvert. */
  onRefreshSource?: () => Promise<string | null>;
  sourceUrl: string;
};

export function CodegenDocxPanelPreview({ filename, onRefreshSource, sourceUrl }: CodegenDocxPanelPreviewProps) {
  const [blob, setBlob] = useState<Blob | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<'generating' | 'ready' | 'error'>('generating');
  const [retryToken, setRetryToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setStatus('generating');
    setError(null);
    setBlob(null);

    (async () => {
      try {
        // URL présignée du stockage : jamais de cookie.
        const response = await fetch(sourceUrl, { credentials: 'omit' });
        if (!response.ok) {
          throw new Error('preview_fetch_failed');
        }
        const nextBlob = await response.blob();
        if (cancelled) {
          return;
        }
        setBlob(nextBlob.type ? nextBlob : new Blob([nextBlob], { type: DOCX_MIME_TYPE }));
        setStatus('ready');
      } catch {
        if (!cancelled) {
          setStatus('error');
          setError(`Impossible de charger l’aperçu de ${filename}.`);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [filename, retryToken, sourceUrl]);

  return (
    <DocxPreviewRenderer
      blob={blob}
      error={error}
      onRetry={() => {
        // Une URL expirée ne se rattrape pas en la rappelant : on en demande une neuve d'abord.
        // Si elle change, le panneau reçoit la nouvelle `sourceUrl` ; sinon le jeton relance le chargement.
        void (onRefreshSource?.() ?? Promise.resolve(null))
          .catch(() => null)
          .finally(() => setRetryToken((value) => value + 1));
      }}
      status={status}
    />
  );
}
